import { articlesTableColumns, withoutStockThresholds } from "@/components/stock";
import { useArticles } from "@/hooks/use-articles";
import { DataTable } from "@msuite/ui/table";
import { guardArray } from "@msuite/utils/array";
import type { OrderFormSchema } from "@msuite/utils/schemas";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { OrderFormArticlesMenu } from "./order-form-articles-menu";
import { columns } from "./table-config";

// Ordering is not restocking: the warning and minimum thresholds say when to reorder from a
// supplier, and a site asking for four of something does not need a verdict on the warehouse's own
// levels. What it needs — the balance, and what is still orderable — stays.
const tableColumns = [...withoutStockThresholds(articlesTableColumns), ...columns];

export function OrderFormArticles() {
	/** Hooks */
	const { articles, isLoading, queryKey, refetch } = useArticles();
	const methods = useFormContext<OrderFormSchema>();
	// Watched as a string of ids, so the table gets a new list only when an article is added or
	// removed. A new list sends it back to page 1, and the array itself is a fresh copy after every
	// quantity write — including the one each row's input makes as it mounts on a page turn.
	const formArticleIds = useWatch({
		control: methods.control,
		name: "articles",
		compute: (formArticles) =>
			guardArray(formArticles)
				.map((formArticle) => formArticle.articleId)
				.join(","),
	});

	const includedArticles = useMemo(() => {
		const includedIds = new Set(formArticleIds.split(","));
		return guardArray(articles).filter((article) => includedIds.has(article.id));
	}, [formArticleIds, articles]);

	/** Render */
	return (
		<DataTable
			data={guardArray(includedArticles)}
			columns={tableColumns}
			isLoading={isLoading}
			refetch={refetch}
			queryKey={queryKey}
			menu={<OrderFormArticlesMenu />}
			persistenceKey="data-order-articles-table"
			initialShowGroupingHeader={false}
			initialPinningState={{
				start: ["number", "name"],
				end: ["quantity", "hint"],
			}}
		/>
	);
}
